import React, { useLayoutEffect, useRef } from 'react';

// Animate the outer wrappers so card transforms remain available to drag/drop.
export default function AnimatedPipelineList({ items, motionKey, children }) {
  const listRef = useRef(null);
  const previous = useRef(new Map());
  const lastKey = useRef(motionKey);
  const running = useRef([]);

  useLayoutEffect(() => {
    const list = listRef.current;
    const clear = () => {
      running.current.forEach(({ animation, ghost }) => { animation.cancel(); ghost?.remove(); });
      running.current = [];
    };
    clear();
    const rect = list.getBoundingClientRect();
    const current = new Map();
    const nodes = [...list.children].filter(node => node.dataset.pipelineCard);
    const changed = lastKey.current !== motionKey;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const animate = (node, frames, options, ghost) => {
      if (!node.animate) { ghost?.remove(); return; }
      const animation = node.animate(frames, options);
      running.current.push({ animation, ghost });
      animation.onfinish = () => ghost?.remove();
    };
    nodes.forEach((node, index) => {
      const bounds = node.getBoundingClientRect();
      const position = { top: bounds.top - rect.top, left: bounds.left - rect.left };
      const old = previous.current.get(node.dataset.pipelineCard);
      if (changed && !reduced) {
        const dy = old ? old.top - position.top : 10;
        const dx = old ? old.left - position.left : 0;
        if (!old || dx || dy) animate(node, [
          { transform: `translate(${dx}px, ${dy}px)`, opacity: old ? 1 : 0 },
          { transform: 'translate(0, 0)', opacity: 1 },
        ], { duration: 240, easing: 'cubic-bezier(.2,.8,.2,1)', delay: old ? 0 : Math.min(index * 18, 90) });
      }
      current.set(node.dataset.pipelineCard, { ...position, width: bounds.width, clone: node.cloneNode(true) });
    });
    if (changed && !reduced) previous.current.forEach((old, id) => {
      if (current.has(id)) return;
      const ghost = old.clone;
      ghost.removeAttribute('data-pipeline-card');
      ghost.setAttribute('aria-hidden', 'true');
      ghost.setAttribute('inert', '');
      ghost.querySelectorAll('[id], [data-testid]').forEach(node => { node.removeAttribute('id'); node.removeAttribute('data-testid'); });
      Object.assign(ghost.style, { position: 'absolute', top: `${old.top}px`, left: `${old.left}px`, width: `${old.width}px`, pointerEvents: 'none', margin: '0', zIndex: '1' });
      list.appendChild(ghost);
      animate(ghost, [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-6px)' }], { duration: 140, easing: 'ease-out', fill: 'forwards' }, ghost);
    });
    previous.current = current;
    lastKey.current = motionKey;
  });

  useLayoutEffect(() => () => {
    running.current.forEach(({ animation, ghost }) => { animation.cancel(); ghost?.remove(); });
  }, []);

  return <div ref={listRef} className="relative p-3 space-y-2">
    {items.map(item => <div key={item.id} data-pipeline-card={item.id}>{children(item)}</div>)}
  </div>;
}
